import React, { useState } from 'react';
import { StarIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function FourthHorsemanWellsPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-556">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-557">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-558">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-559">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-560" />
            <span data-id="element-561">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-562"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-563">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-564">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-565">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-566">
              The Fourth Horseman Rides Where the Wells Run Dry
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-567">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-568" />
            <div className="text-left space-y-1" data-id="element-569">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-570">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-571">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-572">
                May 5, 2026 · 4 min read
              </p>
            </div>
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-573">
            <p data-id="element-574">
              The fourth horseman of Revelation is the one most people
              misremember.
            </p>

            <blockquote className="border-l-4 border-[#b91c1c] pl-6 py-2 italic text-[#0f172a]" data-id="element-575">
              "And I looked, and behold a pale horse: and his name that sat on
              him was Death, and Hell followed with him. And power was given
              unto them over the fourth part of the earth, to kill with sword,
              with hunger, with death, and with the beasts of the earth." —
              Revelation 6:8
            </blockquote>

            <p data-id="element-576">
              <b data-id="element-577">Sword. Hunger. Death.</b> The order matters. The fourth
              horseman doesn't kill primarily by violence — that's the second
              horseman's job. He kills by the cumulative collapse of the systems
              that keep a population alive. And the system that fails first,
              before sword, before hunger, is <b data-id="element-578">water</b>.
            </p>
            <p data-id="element-579">
              You don't get hunger without water failure first. Crops don't fail
              because the sun got brighter. They fail because the rain stopped,
              the river dropped, the aquifer pulled away from the pump, the well
              went dry.{' '}
              <b data-id="element-580">Hunger is a downstream symptom. Thirst is upstream.</b>
            </p>

            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f172a] pt-4" data-id="element-581">
              The early signs every well owner ignores
            </h2>

            <p data-id="element-582">
              If you have a well, this is the practical part. There are signs
              the static water level is dropping toward your pump intake — and
              most well owners ignore them for six to twelve months before
              calling a driller. By the time they call, the wait is nine months
              out and the cost has tripled.
            </p>

            <ul className="space-y-4 pl-6 list-disc marker:text-[#b91c1c]" data-id="element-583">
              <li data-id="element-584">
                <b data-id="element-585">Sediment.</b> Grit at the bottom of glasses, sand in the
                filter screens, particulate where there used to be none. The
                pump is reaching deeper than before.
              </li>
              <li data-id="element-586">
                <b data-id="element-587">Sputtering.</b> The faucet pulls air intermittently when
                multiple fixtures run. The pump is briefly losing prime. It's a
                warning shot.
              </li>
              <li data-id="element-588">
                <b data-id="element-589">Pressure cycles changing.</b> The pressure tank cycles more
                frequently or stays at the low end of its range.
              </li>
              <li data-id="element-590">
                <b data-id="element-591">Pump runs longer.</b> The motor's run time per cycle
                increases. If you've lived with the pump long enough to know its
                rhythm, the rhythm has changed.
              </li>
              <li data-id="element-592">
                <b data-id="element-593">Quality changes.</b> The water tastes different — more
                mineralized, slightly metallic. Deeper water is older water.
              </li>
            </ul>

            <p data-id="element-594">
              Two or more of these and the responsible move is calling a driller
              for an assessment before the well goes fully dry. Assessments are
              cheap. Emergency drills are not.
            </p>
            <p data-id="element-595">
              The fourth horseman is given authority over a fourth of the earth.
              Not all of it. What separates the fourth from the three-fourths is
              whose households were prepared in the season before. The signal is
              already visible in counties the news doesn't cover.{' '}
              <b data-id="element-596">Be the household that does something with it.</b>
            </p>
          </div>
        </article>

        {/* 4. 'You Might Also Like' Section */}
        <section className="space-y-8 pt-8 border-t border-gray-200" data-id="element-597">
          <h2 className="text-3xl font-bold text-[#0f172a]" data-id="element-598">
            You Might Also Like:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-599">
            <div className="group cursor-pointer space-y-3" data-id="element-600">
              <a href="https://trk.adsdomredir.com/click/2" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-601">
                <img src="https://cdn.magicpatterns.com/uploads/tfBxRA9s5qgvhpKFp12h5o/unnamed_(7).jpg" alt="The Lost SuperFoods book" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-602" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-603">
                <a href="https://trk.adsdomredir.com/click/2" data-id="element-604">
                  Superfoods That You Can Store Without Refrigeration for Years
                </a>
              </h3>
            </div>

            <div className="group cursor-pointer space-y-3" data-id="element-605">
              <a href="https://trk.adsdomredir.com/click/3" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-606">
                <img src="https://cdn.magicpatterns.com/uploads/aeW4oPiWoeR5Wzq6FuUxBZ/new5notext.png" alt="Vegetable garden" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-607" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-608">
                <a href="https://trk.adsdomredir.com/click/3" data-id="element-609">
                  The Underground Energy Method They Don't Want You To Know
                  About
                </a>
              </h3>
            </div>

            <div className="group cursor-pointer space-y-3" data-id="element-610">
              <a href="https://trk.adsdomredir.com/click/4" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-611">
                <img src="https://cdn.magicpatterns.com/uploads/7ksskNxA12GTBLDP2Kydn9/img18.png" alt="Man holding tools" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-612" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-613">
                <a href="https://trk.adsdomredir.com/click/4" data-id="element-614">
                  Nature-Inspired 3D Solar Sphere Generates Energy On Demand
                  (Watch Video)
                </a>
              </h3>
            </div>
          </div>

          <AuthorSignature data-id="element-615" />
        </section>
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-616">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-617">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-618">
            <div className="relative w-10 h-10" data-id="element-619">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-620" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-621" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-622">
            <p data-id="element-623">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-624">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-625">
                Terms & Conditions
              </button>
              <span data-id="element-626">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-627">
                Privacy Policy
              </button>
              <span data-id="element-628">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-629">
                Contact Us
              </button>
            </p>
            <p data-id="element-630">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-631">
          <div className="absolute inset-0 bg-black/50" data-id="element-632" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-633">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-634">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-635">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-636">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-637" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-638">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-639">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-640">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                  </p>
                </>}
              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-641">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-642">
                    PatriotsInformer Media, LLC is committed to protecting your
                    privacy.
                  </p>
                </>}
              {activeModal === 'contact' && <>
                  <p data-id="element-643">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-644">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-645">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-646">
                      <span className="font-semibold text-[#0f172a]" data-id="element-647">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-648">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}